var _jsxFileName = "/media/data/Sandbox/soapbox-defake/app/soapbox/components/status-content.tsx";
import "core-js/modules/web.dom-collections.iterator.js";
import "core-js/modules/es.string.replace.js";
import classNames from 'clsx';
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router-dom';
import Icon from 'soapbox/components/icon';
import { useSoapboxConfig } from 'soapbox/hooks';
import { addGreentext } from 'soapbox/utils/greentext';
import { onlyEmoji as isOnlyEmoji } from 'soapbox/utils/rich-content';
import { isRtl } from '../rtl';
import Markup from './markup';
import Poll from './polls/poll';
const MAX_HEIGHT = 642; // 20px * 32 (+ 2px padding at the top)

const BIG_EMOJI_LIMIT = 10;

/** Button to expand a truncated status (due to too much content) */
const ReadMoreButton = _ref => {
  let {
    onClick
  } = _ref;
  return /*#__PURE__*/React.createElement("button", {
    className: "flex items-center text-gray-900 dark:text-gray-300 border-0 bg-transparent p-0 pt-2 hover:underline active:underline",
    onClick: onClick,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 27,
      columnNumber: 3
    }
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "status.read_more",
    defaultMessage: "Read more",
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 28,
      columnNumber: 5
    }
  }), /*#__PURE__*/React.createElement(Icon, {
    className: "inline-block h-5 w-5",
    src: require('@tabler/icons/chevron-right.svg'),
    fixedWidth: true,
    __self: this,
    __source: {
      fileName: _jsxFileName,
      lineNumber: 29,
      columnNumber: 5
    }
  }));
};

/** Renders the text content of a status */
const StatusContent = _ref2 => {
  let {
    status,
    onClick,
    collapsable = false,
    translatable
  } = _ref2;
  const history = useHistory();
  const [collapsed, setCollapsed] = useState(false);
  const [onlyEmoji, setOnlyEmoji] = useState(false);
  const node = useRef(null);
  const {
    greentext
  } = useSoapboxConfig();

  const onMentionClick = (mention, e) => {
    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      history.push("/@".concat(mention.acct));
    }
  };

  const onHashtagClick = (hashtag, e) => {
    hashtag = hashtag.replace(/^#/, '').toLowerCase();

    if (e.button === 0 && !(e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      e.stopPropagation();
      history.push("/tags/".concat(hashtag));
    }
  };
  /** For regular links, just stop propogation */


  const onLinkClick = e => {
    e.stopPropagation();
  };

  const updateStatusLinks = () => {
    if (!node.current) return;
    const links = node.current.querySelectorAll('a');
    links.forEach(link => {
      var _link$textContent, _link$previousSibling, _link$previousSibling2;

      // Skip already processed
      if (link.classList.contains('status-link')) return; // Add attributes

      link.classList.add('status-link');
      link.setAttribute('rel', 'nofollow noopener');
      link.setAttribute('target', '_blank');
      const mention = status.mentions.find(mention => link.href === "".concat(mention.url)); // Add event listeners on mentions and hashtags

      if (mention) {
        link.addEventListener('click', onMentionClick.bind(link, mention), false);
        link.setAttribute('title', mention.acct);
      } else if (((_link$textContent = link.textContent) === null || _link$textContent === void 0 ? void 0 : _link$textContent.charAt(0)) === '#' || ((_link$previousSibling = link.previousSibling) === null || _link$previousSibling === void 0 ? void 0 : (_link$previousSibling2 = _link$previousSibling.textContent) === null || _link$previousSibling2 === void 0 ? void 0 : _link$previousSibling2.charAt(link.previousSibling.textContent.length - 1)) === '#') {
        link.addEventListener('click', onHashtagClick.bind(link, link.text), false);
      } else {
        link.setAttribute('title', link.href);
        link.addEventListener('click', onLinkClick.bind(link), false);
      }
    });
  };

  const maybeSetCollapsed = () => {
    if (!node.current) return;

    if (collapsable && onClick && !collapsed && status.spoiler_text.length === 0) {
      if (node.current.clientHeight > MAX_HEIGHT) {
        setCollapsed(true);
      }
    }
  };

  const maybeSetOnlyEmoji = () => {
    if (!node.current) return;
    const only = isOnlyEmoji(node.current, BIG_EMOJI_LIMIT, true);

    if (only !== onlyEmoji) {
      setOnlyEmoji(only);
    }
  };

  useEffect(() => {
    maybeSetCollapsed();
    maybeSetOnlyEmoji();
    updateStatusLinks();
  });
  const parsedHtml = useMemo(() => {
    const html = translatable && status.translation ? status.translation.get('content') : status.contentHtml;

    if (greentext) {
      return addGreentext(html);
    } else {
      return html;
    }
  }, [status.contentHtml, status.translation]);

  if (status.content.length === 0) {
    return null;
  }

  const withSpoiler = status.spoiler_text.length > 0;
  const baseClassName = 'text-gray-900 dark:text-gray-100 break-words text-ellipsis overflow-hidden relative focus:outline-none';
  const content = {
    __html: parsedHtml
  };
  const direction = isRtl(status.search_index) ? 'rtl' : 'ltr';
  const className = classNames(baseClassName, {
    'cursor-pointer': onClick,
    'whitespace-normal': withSpoiler,
    'max-h-[300px]': collapsed,
    'leading-normal big-emoji': onlyEmoji
  });

  if (onClick) {
    const output = [/*#__PURE__*/React.createElement(Markup, {
      ref: node,
      tabIndex: 0,
      key: "content",
      className: className,
      direction: direction,
      dangerouslySetInnerHTML: content,
      lang: status.language || undefined,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 157,
        columnNumber: 7
      }
    })];

    if (collapsed) {
      output.push( /*#__PURE__*/React.createElement(ReadMoreButton, {
        onClick: onClick,
        key: "read-more",
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 169,
          columnNumber: 19
        }
      }));
    }

    const hasPoll = status.poll && typeof status.poll === 'string';

    if (hasPoll) {
      output.push( /*#__PURE__*/React.createElement(Poll, {
        id: status.poll,
        key: "poll",
        status: status.url,
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 174,
          columnNumber: 19
        }
      }));
    }

    return /*#__PURE__*/React.createElement("div", {
      className: classNames({
        'bg-gray-100 dark:bg-primary-800 rounded-md p-4': hasPoll
      }),
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 177,
        columnNumber: 12
      }
    }, output);
  } else {
    const output = [/*#__PURE__*/React.createElement(Markup, {
      ref: node,
      tabIndex: 0,
      key: "content",
      className: classNames(baseClassName, {
        'leading-normal big-emoji': onlyEmoji
      }),
      direction: direction,
      dangerouslySetInnerHTML: content,
      lang: status.language || undefined,
      __self: this,
      __source: {
        fileName: _jsxFileName,
        lineNumber: 180,
        columnNumber: 7
      }
    })];

    if (status.poll && typeof status.poll === 'string') {
      output.push( /*#__PURE__*/React.createElement(Poll, {
        id: status.poll,
        key: "poll",
        status: status.url,
        __self: this,
        __source: {
          fileName: _jsxFileName,
          lineNumber: 194,
          columnNumber: 19
        }
      }));
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, output);
  }
};

export default /*#__PURE__*/React.memo(StatusContent);